import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useState } from "react";

const HeroSection = () => {
  const isMobile = useIsMobile();
  const [showHeroButton, setShowHeroButton] = useState(true);
  
  useEffect(() => {
    const handleFloatingButtonVisibility = (event: CustomEvent) => {
      setShowHeroButton(!event.detail.isVisible);
    };

    window.addEventListener('floatingButtonVisibility', handleFloatingButtonVisibility as EventListener);
    return () => {
      window.removeEventListener('floatingButtonVisibility', handleFloatingButtonVisibility as EventListener);
    };
  }, []);

  const handleCtaClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent any default behavior
    if (isMobile) {
      window.location.href = "tel:1-800-793-9081";
    } else {
      window.location.href = "/contact";
    }
  };

  return (
    <section className="relative min-h-screen flex flex-col">
      {/* Hero Image Container - Mobile only */}
      <div className="relative w-full h-[50vh] md:hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black z-10" />
        <img
          src="/lovable-uploads/0e2e2266-4ce2-4c5c-9e3a-0ec889032bce.png"
          alt="Professional appliance repair technician working on a modern Maytag dryer"
          className="w-full h-full object-cover"
        />
        {/* Overlay text for mobile - Now at the top */}
        <div className="absolute top-0 left-0 right-0 z-20 p-6 text-center bg-gradient-to-b from-black/80 to-transparent">
          <h1 className="text-3xl font-bold mb-2 text-white text-gradient">
            Expert Appliance Repair
          </h1>
          <p className="text-lg text-white/90">
            Greater Toronto Area
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 md:grid md:grid-cols-2 gap-8 -mt-10 md:mt-0 relative z-30">
        <div className="hidden md:block md:order-1">
          <img
            src="/lovable-uploads/0e2e2266-4ce2-4c5c-9e3a-0ec889032bce.png"
            alt="Professional appliance repair technician working on a modern Maytag dryer"
            className="w-full h-auto rounded-lg shadow-xl transform hover:scale-105 transition-all duration-300"
          />
        </div>

        <div className="md:order-2 glass-effect rounded-xl p-6 md:p-8 text-center md:text-left">
          <div className="hidden md:block">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-primary">
              Expert Appliance Repair in Greater Toronto Area
            </h1>
          </div>
          <ul className="text-gray-300 mb-8 space-y-2 text-left">
            <li className="flex items-center gap-2">
              <span className="text-primary">✓</span> Same-day emergency repairs available
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary">✓</span> Certified technicians with 7 years of expertise
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary">✓</span> Upfront pricing with no hidden fees
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary">✓</span> 90-day warranty on parts and labor
            </li>
          </ul>
          <div className="flex flex-col items-center md:items-start gap-4">
            {showHeroButton && (
              <Button
                size="lg"
                className="bg-accent hover:bg-accent-hover text-accent-light px-8 py-6 text-lg w-full md:w-auto transform hover:scale-105 transition-all duration-300 animate-[wiggle_1s_ease-in-out_1s_7,wiggle_1s_ease-in-out_8s_7,wiggle_1s_ease-in-out_15s_7,wiggle_1s_ease-in-out_22s_7]"
                onClick={handleCtaClick}
              >
                <Phone className="mr-2 animate-pulse" /> Schedule a diagnosis
              </Button>
            )}
            <div className="flex flex-col items-center md:items-start">
              <span className="text-lg font-semibold text-accent">1-800-793-9081</span>
              <span className="text-sm text-gray-400">Available 24/7 for Emergency Repairs</span>
            </div>
            <div className="mt-6 space-y-4 text-gray-300">
              <p className="text-sm border-l-2 border-primary pl-4">
                "Ian arrived within hours and fixed our refrigerator the same day. Outstanding service!" - Sarah M.
              </p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="glass-effect p-3 rounded-lg">
                  <span className="block text-2xl font-bold text-primary">8000+</span>
                  <span className="text-xs">Repairs Completed</span>
                </div>
                <div className="glass-effect p-3 rounded-lg">
                  <span className="block text-2xl font-bold text-primary">7</span>
                  <span className="text-xs">Years Experience</span>
                </div>
                <div className="glass-effect p-3 rounded-lg">
                  <span className="block text-2xl font-bold text-primary">4</span>
                  <span className="text-xs">Month Warranty</span>
                </div>
              </div>
              <p className="text-sm">
                Serving the Greater Toronto Area including Mississauga, Brampton, Markham, and Vaughan
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
